/**
 * FORMATTERS
 * Funciones utilitarias para formatear números, monedas, fechas, etc.
 */

/**
 * Formatea un número como moneda USD
 * @param {number} value - Valor a formatear
 * @param {number} decimals - Decimales a mostrar (default: 2)
 * @returns {string} Valor formateado como "$1,234.56"
 */
export const formatCurrency = (value, decimals = 2) => {
  if (value === null || value === undefined || isNaN(value)) return '$0.00';
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value);
};

/**
 * Formatea un precio en SOL (para pares Token/SOL)
 * @param {number} value - Valor en SOL a formatear
 * @param {number} decimals - Decimales a mostrar (default: 9 para SOL)
 * @returns {string} Valor formateado como "0.000000123 SOL"
 */
export const formatSOL = (value, decimals = 9) => {
  if (value === null || value === undefined || isNaN(value)) return '0 SOL';
  
  // Para valores muy pequeños, usar notación científica si es necesario
  if (value !== 0 && Math.abs(value) < 0.000000001) {
    return `${value.toExponential(3)} SOL`;
  }
  
  return `${value.toFixed(decimals)} SOL`;
};

/**
 * Formatea un número como porcentaje
 * @param {number} value - Valor a formatear
 * @param {number} decimals - Decimales a mostrar (default: 2)
 * @param {boolean} showSign - Mostrar signo + para valores positivos (default: true)
 * @returns {string} Valor formateado como "+12.34%"
 */
export const formatPercent = (value, decimals = 2, showSign = true) => {
  if (value === null || value === undefined || isNaN(value)) return '0%';
  
  const sign = showSign && value > 0 ? '+' : '';
  return `${sign}${value.toFixed(decimals)}%`;
};

/**
 * Formatea un número con separadores de miles
 * @param {number} value - Valor a formatear
 * @param {number} decimals - Decimales a mostrar (default: 2)
 * @returns {string} Valor formateado como "1,234.56"
 */
export const formatNumber = (value, decimals = 2) => {
  if (value === null || value === undefined || isNaN(value)) return '0';
  
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value);
};

/**
 * Formatea un número grande con sufijos (K, M, B)
 * @param {number} value - Valor a formatear
 * @returns {string} Valor formateado como "1.2K", "3.4M", etc.
 */
export const formatCompact = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '0';
  
  if (Math.abs(value) >= 1e9) {
    return (value / 1e9).toFixed(1) + 'B';
  }
  if (Math.abs(value) >= 1e6) {
    return (value / 1e6).toFixed(1) + 'M';
  }
  if (Math.abs(value) >= 1e3) {
    return (value / 1e3).toFixed(1) + 'K';
  }
  return value.toFixed(0);
};

/**
 * Formatea una fecha
 * @param {Date|string} date - Fecha a formatear
 * @param {string} format - Formato: 'short', 'long', 'time', 'datetime' (default: 'short')
 * @returns {string} Fecha formateada
 */
export const formatDate = (date, format = 'short') => {
  if (!date) return '';
  
  const d = new Date(date);
  
  if (isNaN(d.getTime())) return '';
  
  const options = {
    short: {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    },
    long: {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    },
    time: {
      hour: '2-digit',
      minute: '2-digit'
    },
    datetime: {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    },
    full: {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }
  };
  
  return d.toLocaleDateString('en-US', options[format] || options.short);
};

/**
 * Formatea tiempo relativo (hace X minutos, hace X horas, etc.)
 * @param {Date|string} date - Fecha a formatear
 * @returns {string} Tiempo relativo como "hace 5 minutos"
 */
export const formatTimeAgo = (date) => {
  if (!date) return '';
  
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  
  const seconds = Math.floor((new Date() - d) / 1000);
  
  const intervals = {
    year: 31536000,
    month: 2592000,
    week: 604800,
    day: 86400,
    hour: 3600,
    minute: 60,
    second: 1
  };
  
  for (const [unit, secondsInUnit] of Object.entries(intervals)) {
    const interval = Math.floor(seconds / secondsInUnit);
    if (interval >= 1) {
      return interval === 1 
        ? `hace 1 ${unit}${interval > 1 ? 's' : ''}`
        : `hace ${interval} ${unit}s`;
    }
  }
  
  return 'ahora mismo';
};

/**
 * Formatea duración en segundos a formato legible
 * @param {number} seconds - Segundos a formatear
 * @returns {string} Duración como "2h 30m"
 */
export const formatDuration = (seconds) => {
  if (!seconds || seconds < 0) return '0s';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (secs > 0 && hours === 0) parts.push(`${secs}s`);
  
  return parts.join(' ') || '0s';
};

/**
 * Formatea un wallet address (acorta con ...)
 * @param {string} address - Dirección de wallet
 * @param {number} startChars - Caracteres al inicio (default: 6)
 * @param {number} endChars - Caracteres al final (default: 4)
 * @returns {string} Dirección acortada como "0x1234...5678"
 */
export const formatAddress = (address, startChars = 6, endChars = 4) => {
  if (!address || address.length <= startChars + endChars) return address;
  
  return `${address.slice(0, startChars)}...${address.slice(-endChars)}`;
};

/**
 * Formatea un símbolo de par de trading
 * @param {string} symbol - Símbolo como "BTCUSDT"
 * @returns {string} Símbolo formateado como "BTC/USDT"
 */
export const formatSymbol = (symbol) => {
  if (!symbol) return '';
  
  // Si ya tiene /, no hacer nada
  if (symbol.includes('/')) return symbol;
  
  // Detectar pares comunes
  const quoteCurrencies = ['USDT', 'BUSD', 'USD', 'BTC', 'ETH', 'BNB'];
  
  for (const quote of quoteCurrencies) {
    if (symbol.endsWith(quote)) {
      const base = symbol.slice(0, -quote.length);
      return `${base}/${quote}`;
    }
  }
  
  return symbol;
};

/**
 * Formatea PnL con color y símbolo
 * @param {number} value - Valor de PnL
 * @param {boolean} percentage - Si es porcentaje o valor absoluto
 * @returns {object} { value: string, className: string, icon: string }
 */
export const formatPnL = (value, percentage = false) => {
  if (value === null || value === undefined || isNaN(value)) {
    return {
      value: percentage ? '0%' : '$0.00',
      className: 'neutral',
      icon: '→'
    };
  }
  
  const isPositive = value > 0;
  const isNegative = value < 0;
  
  return {
    value: percentage ? formatPercent(value) : formatCurrency(value),
    className: isPositive ? 'profit' : isNegative ? 'loss' : 'neutral',
    icon: isPositive ? '↑' : isNegative ? '↓' : '→'
  };
};

/**
 * Capitaliza la primera letra de una cadena
 * @param {string} str - Cadena a capitalizar
 * @returns {string} Cadena capitalizada
 */
export const capitalize = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Trunca un texto largo
 * @param {string} text - Texto a truncar
 * @param {number} maxLength - Longitud máxima
 * @returns {string} Texto truncado con "..."
 */
export const truncate = (text, maxLength = 50) => {
  if (!text || text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

// Exportar todas las funciones como objeto también
export default {
  formatCurrency,
  formatPercent,
  formatNumber,
  formatCompact,
  formatDate,
  formatTimeAgo,
  formatDuration,
  formatAddress,
  formatSymbol,
  formatPnL,
  capitalize,
  truncate
};
